import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, vModelCheckbox as _vModelCheckbox, mergeProps as _mergeProps, withDirectives as _withDirectives, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-2 -mt-0.5" }
const _hoisted_2 = { class: "flex items-center cursor-pointer w-fit" }
const _hoisted_3 = {
  key: 0,
  class: "h-4 w-4 inline-block rounded border border-gray-300 bg-gray-100 cursor-not-allowed"
}
const _hoisted_4 = { class: "flex items-center cursor-pointer w-fit" }
const _hoisted_5 = { class: "ml-2" }
const _hoisted_6 = { class: "flex items-center cursor-pointer w-fit" }
const _hoisted_7 = { class: "ml-2" }
const _hoisted_8 = { class: "flex items-center cursor-pointer w-fit" }
const _hoisted_9 = { class: "ml-2" }
const _hoisted_10 = { class: "flex items-center cursor-pointer w-fit" }
const _hoisted_11 = {
  key: 0,
  class: "h-4 w-4 inline-block rounded border border-gray-300 bg-gray-100 cursor-not-allowed"
}
const _hoisted_12 = { class: "flex items-center justify-between relative" }

import { LockClosedIcon } from "@heroicons/vue/24/outline";
import { processClasses } from "@oculai/planner";
import { useForm } from "vee-validate";
import { computed, watch, ref } from "vue";
import { useI18n } from "vue-i18n";
import SelectList from "@/components/forms/SelectList.vue";
import Tooltip from "@/components/tooltips/TooltipBasic.vue";
import { useHasProjectFeature } from "@/composables/project";
import { HierarchyTagStore } from "@/types/HierarchyTag";
import { CycleTimesReportConfig, ReportFilter, ReportProcessFilter } from "@/types/Report";


export default /*#__PURE__*/_defineComponent({
  __name: 'CycleTimesConfig',
  props: {
    config: {},
    filters: {},
    hierarchyTags: {}
  },
  emits: ["configChange"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();

const initialValues = computed(() => ({
  aggregationLevel: props.config.aggregation_level,
  showPlanned: props.config.show_planned,
  showOutages: props.config.show_outages,
  showUtilization: props.config.show_utilization,
}));

const availableHierarchyTags = ref<string[]>([]);
const showPlannedEnabled = ref(false);

const { defineField } = useForm({
  initialValues: initialValues.value,
});

const [aggregationLevel] = defineField("aggregationLevel");
const [showPlanned, showPlannedProps] = defineField("showPlanned");
const [showUtilization, showUtilizationProps] = defineField("showUtilization");
const [showOutages, showOutagesProps] = defineField("showOutages");
const [showActiveDays, showActiveDaysProps] = defineField("showActiveDays");
const [showInactiveDays, showInactiveDaysProps] = defineField("showInactiveDays");

watch(
  [() => props.config, () => props.filters],
  () => {
    const reportProcessFilter = props.filters.find((filter) => {
      return filter.type === "processes";
    }) as ReportProcessFilter;

    aggregationLevel.value = props.config.aggregation_level;
    showPlanned.value = props.config.show_planned;
    showUtilization.value = props.config.show_utilization;
    showOutages.value = props.config.show_outages;
    showActiveDays.value = props.config.show_active_days;
    showInactiveDays.value = props.config.show_inactive_days;

    availableHierarchyTags.value = Array.from(
      new Set(props.hierarchyTags.map((tag) => tag.type).filter((tag) => tag !== "component")),
    ).sort((a, b) =>
      a === "building" ? -1 : b === "building" ? 1 : a === "level" ? -1 : b === "level" ? 1 : 0,
    );

    let shouldShowPlannedChart = false;

    if (
      reportProcessFilter.value.mode === "single_process" &&
      (!reportProcessFilter.value.single_processes.length ||
        reportProcessFilter.value.single_processes.length === processClasses.length)
    ) {
      shouldShowPlannedChart = true;
    } else if (reportProcessFilter.value.mode === "component") {
      const isAggregatedBySection = props.config.aggregation_level === "section";
      const isAggregatedByPreset =
        props.hierarchyTags.filter((tag) => {
          const componentToCompare = reportProcessFilter.value.components.find((component) => {
            return component.id === tag._id;
          });

          if (!componentToCompare) {
            return false;
          }

          const isAllProcessesSelected = tag.attached_processes.every((process) => {
            return componentToCompare.processes.includes(process);
          });

          return tag.type === "component" && isAllProcessesSelected;
        }).length === 1;

      const noProcessSelected = !reportProcessFilter.value.components.some((component) => {
        return component.processes.length;
      });

      shouldShowPlannedChart = (isAggregatedBySection && isAggregatedByPreset) || noProcessSelected;
    }

    showPlannedEnabled.value = shouldShowPlannedChart;
  },
  { immediate: true },
);

const handleConfigChange = () => {
  const config: CycleTimesReportConfig = {
    aggregation_level: aggregationLevel.value,
    show_planned: showPlanned.value,
    show_outages: showOutages.value,
    show_utilization: showUtilization.value,
    show_active_days: showActiveDays.value,
    show_inactive_days: showInactiveDays.value,
  };
  emit("configChange", config);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(SelectList, {
      options: 
        availableHierarchyTags.value.map((tag) => ({
          name: _unref(t)(`analytics.reports.${tag}`),
          value: tag,
        }))
      ,
      name: "aggregationLevel",
      defaultSelected: _unref(aggregationLevel),
      label: _unref(t)('analytics.reports.aggregation_level'),
      class: "mb-3",
      "onUpdate:selected": (value) => (aggregationLevel.value = value, handleConfigChange()) 
    }, null, 8, ["options", "defaultSelected", "label", "onUpdate:selected"]),
    _createElementVNode("label", _hoisted_2, [
      (!showPlannedEnabled.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3))
        : _withDirectives((_openBlock(), _createElementBlock("input", _mergeProps({
            key: 1,
            type: "checkbox",
            class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(showPlanned) ? (showPlanned).value = $event : null))
          }, _unref(showPlannedProps), { onChange: handleConfigChange }), null, 16)), [
            [_vModelCheckbox, _unref(showPlanned)]
          ]),
      _createElementVNode("span", {
        class: _normalizeClass(['ml-2', !showPlannedEnabled.value && 'text-gray-400'])
      }, _toDisplayString(_unref(t)("analytics.reports.show_planned")), 3)
    ]),
    _createElementVNode("label", _hoisted_4, [
      _withDirectives(_createElementVNode("input", _mergeProps({
        type: "checkbox",
        class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(showActiveDays) ? (showActiveDays).value = $event : null))
      }, _unref(showActiveDaysProps), { onChange: handleConfigChange }), null, 16), [
        [_vModelCheckbox, _unref(showActiveDays)]
      ]),
      _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)("working_day.active_days", 2)), 1)
    ]),
    _createElementVNode("label", _hoisted_6, [
      _withDirectives(_createElementVNode("input", _mergeProps({
        type: "checkbox",
        class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(showInactiveDays) ? (showInactiveDays).value = $event : null))
      }, _unref(showInactiveDaysProps), { onChange: handleConfigChange }), null, 16), [
        [_vModelCheckbox, _unref(showInactiveDays)]
      ]),
      _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)("working_day.inactive_days", 2)), 1)
    ]),
    _createElementVNode("label", _hoisted_8, [
      _withDirectives(_createElementVNode("input", _mergeProps({
        type: "checkbox",
        class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(showOutages) ? (showOutages).value = $event : null))
      }, _unref(showOutagesProps), { onChange: handleConfigChange }), null, 16), [
        [_vModelCheckbox, _unref(showOutages)]
      ]),
      _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)("working_day.outage")), 1)
    ]),
    _createElementVNode("label", _hoisted_10, [
      (!_unref(useHasProjectFeature)('working_hours'))
        ? (_openBlock(), _createElementBlock("div", _hoisted_11))
        : _withDirectives((_openBlock(), _createElementBlock("input", _mergeProps({
            key: 1,
            type: "checkbox",
            class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer",
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(showUtilization) ? (showUtilization).value = $event : null))
          }, _unref(showUtilizationProps), { onChange: handleConfigChange }), null, 16)), [
            [_vModelCheckbox, _unref(showUtilization)]
          ]),
      _createElementVNode("span", {
        class: _normalizeClass(['ml-2', !_unref(useHasProjectFeature)('working_hours') && 'text-gray-400'])
      }, _toDisplayString(_unref(t)("analytics.reports.show_utilization")), 3),
      _createElementVNode("div", _hoisted_12, [
        (!_unref(useHasProjectFeature)('working_hours'))
          ? (_openBlock(), _createBlock(Tooltip, {
              key: 0,
              content: _ctx.$t('paywall.working_hours'),
              "wrapper-class": "overflow-hidden text-ellipsis ml-2",
              positionHorizontal: "left",
              positionVertical: "top"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(LockClosedIcon), { class: "h-4 w-4" })
              ]),
              _: 1
            }, 8, ["content"]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})